import React, { useEffect, useMemo, useState, useRef } from "react";
import { IP } from "../configuracion";

// Función para validar datos del cheque antes de cargar
const validarDatosCheque = async (numeroCheque, property) => {
    try {
        console.log(`[OrderSummary] Iniciando validación para cheque: ${numeroCheque}, property: ${property}`);
        
        if (!numeroCheque || !property) {
            throw new Error('Número de cheque y property son requeridos');
        }

        const baseUrl = `${IP}/tavpos/backend/validacion_datos.php`;
        const params = new URLSearchParams({
            property: property,
            numero_cheque: numeroCheque.toString(),
            t: new Date().getTime().toString()
        });
        
        const url = `${baseUrl}?${params}`;
        console.log(`[OrderSummary] URL de validación: ${url}`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'omit',
                signal: controller.signal,
                mode: 'cors'
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                let errorText = 'Error desconocido';
                try {
                    errorText = await response.text();
                } catch (e) {
                    console.error('[OrderSummary] Error leyendo respuesta:', e);
                }
                
                const errorMessages = {
                    400: 'Solicitud incorrecta - verifique los parámetros',
                    404: 'Servicio de validación no encontrado',
                    500: 'Error interno del servidor',
                    503: 'Servicio no disponible'
                };
                
                const errorMessage = errorMessages[response.status] || `Error del servidor: ${response.status}`;
                throw new Error(errorMessage);
            }

            let responseData;
            try {
                responseData = await response.json();
            } catch (parseError) {
                console.error('[OrderSummary] Error parseando JSON:', parseError);
                throw new Error('Respuesta inválida del servidor - formato JSON incorrecto');
            }

            if (responseData && typeof responseData === 'object') {
                if (responseData.success === true) {
                    return {
                        success: true,
                        data: responseData.data,
                        message: responseData.message || 'Validación completada exitosamente'
                    };
                } else {
                    throw new Error(responseData.message || 'Error en la validación de datos');
                }
            } else {
                throw new Error('Respuesta del servidor en formato incorrecto');
            }

        } catch (fetchError) {
            clearTimeout(timeoutId);
            
            if (fetchError.name === 'AbortError') {
                throw new Error('La validación tardó demasiado tiempo (timeout)');
            } else if (fetchError.name === 'TypeError') {
                if (fetchError.message.includes('Failed to fetch')) {
                    throw new Error('Error de conexión - no se pudo contactar al servidor');
                } else {
                    throw new Error(`Error de red: ${fetchError.message}`);
                }
            }
            throw fetchError;
        }

    } catch (error) {
        console.error('[OrderSummary] Error completo en validación de datos:', {
            message: error.message,
            cheque: numeroCheque,
            property: property,
            stack: error.stack
        });
        
        return {
            success: false,
            message: error.message,
            data: null,
            error: error.name
        };
    }
};

// Función para obtener valores por defecto y convertir a número
const getSafeValue = (value, defaultValue = 0) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const normalizedValue = value.replace(',', '.');
    const parsed = parseFloat(normalizedValue);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

// ✅ FUNCIÓN PARA CALCULAR TOTAL DE UN ITEM
const calculateItemTotal = (item) => {
  if (!item) {
    return {
      baseSubtotal: 0,
      totalDiscount: 0,
      subtotalBeforeTaxes: 0,
      taxAmount: 0,
      cargoAmount: 0,
      totalWithTaxes: 0
    };
  }
  
  if (item.isCancelled || item.isVoided || item.quantity < 0) {
    return {
      baseSubtotal: 0,
      totalDiscount: 0,
      subtotalBeforeTaxes: 0,
      taxAmount: 0,
      cargoAmount: 0,
      totalWithTaxes: 0
    };
  }
  
  try {
    const itemPrice = Math.abs(getSafeValue(item.price, 0));
    const itemQuantity = Math.abs(getSafeValue(item.quantity, 1));
    const taxPercent = getSafeValue(item.tax, 0);
    let cargoPercent = getSafeValue(item.cargo, 0);
    if (cargoPercent === 0) cargoPercent = getSafeValue(item.cargo_servicio, 0);
    if (cargoPercent === 0) cargoPercent = getSafeValue(item.cs, 0);
    
    const totalIncludedPercent = 1 + (taxPercent / 100) + (cargoPercent / 100);
    const subtotalBasePorUnidad = itemPrice / totalIncludedPercent;
    let baseSubtotalProducto = subtotalBasePorUnidad * itemQuantity;
    
    let modifiersSubtotalBase = 0;
    let modifiersTaxAmount = 0;
    let modifiersCargoAmount = 0;
    let modifiersDiscountTotal = 0;
    
    if (Array.isArray(item.modifiers) && item.modifiers.length > 0) {
      item.modifiers.forEach((modifier) => {
        let modifierItems = [];
        if (Array.isArray(modifier.items)) {
          modifierItems = modifier.items;
        } else if (modifier.name || modifier.price) {
          modifierItems = [modifier];
        }
        
        modifierItems.forEach((mod) => {
          const modPrice = Math.abs(getSafeValue(mod.price, 0));
          const modQuantity = Math.abs(getSafeValue(mod.quantity, 1));
          const modTaxPercent = getSafeValue(mod.tax, taxPercent);
          let modCargoPercent = getSafeValue(mod.cargo, cargoPercent);
          if (modCargoPercent === 0) modCargoPercent = getSafeValue(mod.cargo_servicio, cargoPercent);
          if (modCargoPercent === 0) modCargoPercent = getSafeValue(mod.cs, cargoPercent);
          
          const modIncludedPercent = 1 + (modTaxPercent / 100) + (modCargoPercent / 100);
          const modSubtotalBase = modPrice / modIncludedPercent;
          
          let modDiscount = Math.abs(getSafeValue(mod.monto_descuento, 0));
          if (modDiscount === 0 && mod.descuento_porcentaje > 0) {
            modDiscount = (modSubtotalBase * modQuantity * mod.descuento_porcentaje) / 100;
          }
          
          modifiersDiscountTotal += modDiscount;
          const modSubtotalConDescuento = (modSubtotalBase * modQuantity) - modDiscount;
          modifiersSubtotalBase += modSubtotalConDescuento;
          
          const modBaseSinDescuento = modSubtotalBase * modQuantity;
          modifiersTaxAmount += (modBaseSinDescuento * modTaxPercent) / 100;
          modifiersCargoAmount += (modBaseSinDescuento * modCargoPercent) / 100;
        });
      });
    }
    
    let productDiscountAmount = 0;
    
    if (item.monto_descuento && item.monto_descuento !== 0) {
      productDiscountAmount = Math.abs(getSafeValue(item.monto_descuento, 0));
    } 
    else if (item.discount) {
      productDiscountAmount = Math.abs(getSafeValue(item.discount.monto, 0));
      if (productDiscountAmount === 0 && item.discount.price > 0) {
        productDiscountAmount = (baseSubtotalProducto * item.discount.price) / 100;
      }
    }
    else if (item.descuento_porcentaje && item.descuento_porcentaje > 0) {
      productDiscountAmount = (baseSubtotalProducto * item.descuento_porcentaje) / 100;
    }
    
    // ✅ FIX: precio×qty (inclusive) - descuento = neto, luego extraer base/impuesto/cargo del neto
    // El descuento cubre primero el producto; si excede, el sobrante reduce modificadores
    // incluyendo su parte proporcional de impuesto y cargo.
    const inclusiveTotalProducto = itemPrice * itemQuantity;
    const productOnlyDiscount = Math.min(productDiscountAmount, inclusiveTotalProducto);
    const modifierExcessDiscount = Math.max(0, productDiscountAmount - inclusiveTotalProducto);

    const inclusiveAfterDiscount = Math.max(0, inclusiveTotalProducto - productOnlyDiscount);
    const subtotalProductoConDescuento = inclusiveAfterDiscount / totalIncludedPercent;

    // Reducir modificadores (subtotal + impuesto + cargo) proporcionalmente al exceso
    let adjModifiersSubtotal = modifiersSubtotalBase;
    let adjModifiersTax = modifiersTaxAmount;
    let adjModifiersCargo = modifiersCargoAmount;
    if (modifierExcessDiscount > 0 && modifiersSubtotalBase > 0) {
      const ratio = Math.min(1, modifierExcessDiscount / modifiersSubtotalBase);
      adjModifiersSubtotal = Math.max(0, modifiersSubtotalBase - modifierExcessDiscount);
      adjModifiersTax    = modifiersTaxAmount   * (1 - ratio);
      adjModifiersCargo  = modifiersCargoAmount * (1 - ratio);
    }

    const subtotalBaseTotal = subtotalProductoConDescuento + adjModifiersSubtotal;
    const productTaxAmount = (subtotalProductoConDescuento * taxPercent) / 100;
    const productCargoAmount = (subtotalProductoConDescuento * cargoPercent) / 100;

    const taxAmountTotal = productTaxAmount + adjModifiersTax;
    const cargoAmountTotal = productCargoAmount + adjModifiersCargo;
    const totalDiscountAmount = productDiscountAmount + modifiersDiscountTotal;
    
    let totalWithTaxes = subtotalBaseTotal + taxAmountTotal + cargoAmountTotal;
    
    return {
      baseSubtotal: Number(baseSubtotalProducto.toFixed(6)),
      totalDiscount: Number(totalDiscountAmount.toFixed(6)),
      subtotalBeforeTaxes: Number(subtotalBaseTotal.toFixed(6)),
      taxAmount: Number(taxAmountTotal.toFixed(6)),
      cargoAmount: Number(cargoAmountTotal.toFixed(6)),
      totalWithTaxes: Number(totalWithTaxes.toFixed(6))
    };
    
  } catch (error) {
    console.error("Error en calculateItemTotal:", error, item);
    return {
      baseSubtotal: 0,
      totalDiscount: 0,
      subtotalBeforeTaxes: 0,
      taxAmount: 0,
      cargoAmount: 0,
      totalWithTaxes: 0
    };
  }
};

const getStatusStyle = (estado, esProductoSinOrdenar = false) => {
  if (esProductoSinOrdenar) {
    return {
      text: "SIN ORDENAR",
      backgroundColor: "#9C27B0",
      color: "white",
    };
  }

  switch (estado?.toLowerCase()) {
    case "producido":
      return {
        text: "PRODUCIDO",
        backgroundColor: "#4CAF50",
        color: "white",
      };
    case "pendiente":
      return {
        text: "PENDIENTE",
        backgroundColor: "#FFC107",
        color: "black",
      };
    case "void":
      return {
        text: "ANULADO",
        backgroundColor: "#ff4757",
        color: "white",
      };
    default:
      return {
        text: "CANCELADO",
        backgroundColor: "#ff4757",
        color: "white",
      };
  }
};

const DiscountDisplay = ({ discount, totalDiscount, itemQuantity }) => {
  if (totalDiscount <= 0 || itemQuantity < 0) return null;

  return (
    <div style={styles.discountLine}>
      <span style={styles.discountLabel}>
        Descuentos {discount?.nombre_descuento ? `(${discount.nombre_descuento})` : ""}:
      </span>
      <span style={styles.discountValue}>-${Math.abs(totalDiscount).toFixed(2)}</span>
    </div>
  );
};

// Requerimiento "Notas": las notas se guardan como un modificador especial
// (id "mod_note_...", ver handleAddOpenNote en App.js) para no tocar el
// backend/KDS/impresión, que ya sabe leer modificadores. Aquí solo se
// identifican para poder mostrarlas con estilo propio, separadas de los
// modificadores reales — no cambia qué se envía ni cómo se calcula el total
// (las notas ya viajan con price:0, igual que antes).
const NOTE_MOD_PREFIX = "mod_note_";
const isNoteModifier = (mod) => typeof mod?.id === "string" && mod.id.startsWith(NOTE_MOD_PREFIX);

const NoteDisplay = ({ notes }) => {
  if (!Array.isArray(notes) || notes.length === 0) return null;
  return (
    <div style={styles.itemNote} data-role="order-note">
      <span style={styles.itemNoteIcon} aria-hidden="true">📝</span>
      <div style={styles.itemNoteTextWrap}>
        {notes.map((n, i) => (
          <div key={n.id || i} style={styles.itemNoteText}>{n.name}</div>
        ))}
      </div>
    </div>
  );
};

const ModifiersDisplay = ({ modifiers, itemQuantity }) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0 || itemQuantity < 0) {
    return null;
  }

  const noteModifiers = modifiers.filter(isNoteModifier);
  const realModifiers = modifiers.filter((m) => !isNoteModifier(m));

  const calculateModifierTotal = (modifier) => {
    let modifierItems = [];
    if (Array.isArray(modifier.items)) {
      modifierItems = modifier.items;
    } else if (modifier.name || modifier.price) {
      modifierItems = [modifier];
    }
    
    return modifierItems.reduce((total, mod) => {
      const modPrice = Math.abs(getSafeValue(mod.price, 0));
      const modQuantity = Math.abs(getSafeValue(mod.quantity, 1));
      const modDiscount = Math.abs(getSafeValue(mod.monto_descuento, 0));
      const modPercentageDiscount = Math.abs(getSafeValue(mod.descuento_porcentaje, 0));
      const modSubtotal = modPrice * modQuantity;
      const discountAmount = modDiscount + (modSubtotal * modPercentageDiscount) / 100;
      const finalPrice = modSubtotal - discountAmount;
      return total + finalPrice;
    }, 0);
  };

  const getModifierName = (modifier) => {
    return modifier.name || modifier.nombre || "Modificador sin nombre";
  };

  const getModifierQuantity = (modifier) => {
    if (Array.isArray(modifier.items)) {
      return modifier.items.reduce((sum, item) => sum + getSafeValue(item.quantity, 1), 0);
    }
    return getSafeValue(modifier.quantity, 1);
  };

  const isGroupStructure = realModifiers.some(mod => Array.isArray(mod.items));

  return (
    <>
      {realModifiers.length > 0 && (
        <div style={styles.itemModifiers}>
          <strong style={{ fontSize: "0.9em" }}>Modificadores:</strong>
          <ul style={styles.modifiersList}>
            {isGroupStructure
              ? realModifiers.flatMap((group, groupIndex) => {
                  if (!Array.isArray(group.items) || group.items.length === 0) {
                    return [];
                  }
                  return group.items.map((item, itemIndex) => {
                    const modTotal = calculateModifierTotal(item);
                    return (
                      <li key={`${groupIndex}-${itemIndex}`} style={styles.modifierItem}>
                        <span>{getSafeValue(item.quantity, 1)}x {getModifierName(item)}</span>
                        <span>{modTotal > 0 ? `+$${modTotal.toFixed(2)}` : "(Gratis)"}</span>
                      </li>
                    );
                  });
                })
              : realModifiers.map((modifier, modIndex) => {
                  const modTotal = calculateModifierTotal(modifier);
                  return (
                    <li key={modIndex} style={styles.modifierItem}>
                      <span>{getModifierQuantity(modifier)}x {getModifierName(modifier)}</span>
                      <span>{modTotal > 0 ? `+$${modTotal.toFixed(2)}` : "(Gratis)"}</span>
                    </li>
                  );
                })}
          </ul>
        </div>
      )}
      <NoteDisplay notes={noteModifiers} />
    </>
  );
};

const ExpandableDetails = ({ item, totalWithTaxes, originalItem }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!item) {
    return null;
  }
  
  const isCancelledOrVoided = item.isCancelled || item.isVoided || item.quantity < 0;
  
  const itemTotal = isCancelledOrVoided ? { subtotalBeforeTaxes: 0, taxAmount: 0, cargoAmount: 0, totalDiscount: 0 } : calculateItemTotal(item);
  const subtotalBeforeTaxes = itemTotal?.subtotalBeforeTaxes ?? 0;
  const taxAmount = itemTotal?.taxAmount ?? 0;
  const cargoAmount = itemTotal?.cargoAmount ?? 0;
  const totalDiscount = itemTotal?.totalDiscount ?? 0;
  
  let cargoValue = getSafeValue(item.cargo, 0);
  if (cargoValue === 0) cargoValue = getSafeValue(item.cargo_servicio, 0);
  if (cargoValue === 0) cargoValue = getSafeValue(item.cs, 0);
  
  const displayTotal = isCancelledOrVoided ? 0 : (totalWithTaxes !== undefined ? totalWithTaxes : (itemTotal?.totalWithTaxes ?? 0));

  return (
    <div>
      <div 
        style={{ 
          textAlign: "right", 
          marginTop: "8px",
          cursor: !isCancelledOrVoided ? "pointer" : "default",
          fontWeight: "600",
          color: item.isVoided ? "#ff4757" : (isCancelledOrVoided ? "#e74c3c" : "#1976d2"),
          fontSize: "0.9em",
          padding: "4px",
          borderRadius: "4px",
          backgroundColor: item.isVoided ? "#ffebee" : (isCancelledOrVoided ? "#ffeaea" : "#f0f8ff"),
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isCancelledOrVoided) setExpanded(!expanded);
        }}
      >
        Total: ${displayTotal?.toFixed(2) || '0.00'} {!isCancelledOrVoided && (expanded ? "▲" : "▼")}
        {isCancelledOrVoided && (
          <span style={{ marginLeft: "8px", fontSize: "0.85em" }}>
            {item.isVoided ? "(ANULADO - Valor 0)" : "(CANCELADO - Valor 0)"}
          </span>
        )}
      </div>
      
      {isCancelledOrVoided && originalItem && (
        <div style={{ 
          marginTop: "8px", 
          padding: "8px", 
          backgroundColor: "#fff3cd", 
          borderRadius: "4px",
          fontSize: "0.8em",
          borderLeft: "3px solid #ffc107"
        }}>
          <strong>⚠️ Este registro {item.isVoided ? "ANULA" : "CANCELA"} al producto original:</strong><br />
          <span style={{ textDecoration: "line-through", color: "#856404" }}>
            {originalItem.name} - {Math.abs(originalItem.quantity)} x ${originalItem.price.toFixed(2)} = ${(originalItem.price * Math.abs(originalItem.quantity)).toFixed(2)}
          </span>
          <div style={{ marginTop: "4px", fontSize: "0.85em", color: "#d32f2f" }}>
            ➡️ El producto original queda con valor $0.00
          </div>
        </div>
      )}
           
      {expanded && !isCancelledOrVoided && (
        <div style={styles.expandableSection}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
            <span>Subtotal:</span>
            <span>${Math.abs(subtotalBeforeTaxes || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
            <span>Impuesto ({getSafeValue(item.tax, 0)}%):</span>
            <span>${Math.abs(taxAmount || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
            <span>Cargo por servicio ({cargoValue}%):</span>
            <span>${Math.abs(cargoAmount || 0).toFixed(2)}</span>
          </div>
          {Math.abs(totalDiscount || 0) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", color: "#e53935", marginBottom: "5px", fontWeight: "600" }}>
              <span>Descuentos:</span>
              <span>-${Math.abs(totalDiscount || 0).toFixed(2)}</span>
            </div>
          )}
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            fontWeight: "bold", 
            marginTop: "8px", 
            paddingTop: "8px", 
            borderTop: "1px solid #dee2e6", 
            color: "#2c3e50" 
          }}>
            <span>Total:</span>
            <span>${displayTotal?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  orderSummary: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    width: "100%",
    maxWidth: "100%",
    margin: "0",
    padding: "20px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: "1px solid #e0e0e0",
    boxSizing: "border-box",
  },
  headerWithSelectAll: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "2px solid #3498db",
    flexWrap: "wrap",
    gap: "10px",
  },
  selectAllButton: {
    padding: "6px 12px",
    backgroundColor: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  selectAllButtonSelected: {
    backgroundColor: "#4CAF50",
  },
  selectAllButtonDeselect: {
    backgroundColor: "#f44336",
  },
  selectionInfo: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  orderItem: {
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "12px",
    position: "relative",
    backgroundColor: "#fafafa",
    transition: "all 0.2s ease",
    cursor: "pointer",
  },
  selectedMultiple: {
    backgroundColor: "#e3f2fd",
    border: "2px solid #4CAF50",
    boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
    position: "relative",
  },
  selectedMultipleBadge: {
    position: "absolute",
    top: "8px",
    left: "8px",
    backgroundColor: "#4CAF50",
    color: "white",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "10px",
    fontWeight: "bold",
  },
  voidedItem: {
    backgroundColor: "#ffebee",
    borderLeft: "4px solid #ff4757",
  },
  cancelledItem: {
    backgroundColor: "#fff5f5",
    borderLeft: "4px solid #ff4757",
    color: "#666",
  },
  unsavedItem: {
    backgroundColor: "#f3e5f5",
    borderLeft: "4px solid #9C27B0",
  },
  itemModifiers: {
    marginLeft: "12px",
    marginTop: "8px",
    fontSize: "0.85em",
    color: "#555",
  },
  modifiersList: {
    listStyle: "none",
    padding: "0",
    margin: "6px 0 0 0",
  },
  modifierItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "4px",
    padding: "2px 0",
  },
  // Requerimiento "Notas": estilo propio para distinguir la nota aplicada
  // de los modificadores reales, cantidades o precios.
  itemNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 10px",
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe082",
    borderLeft: "3px solid #f9a825",
    borderRadius: "6px",
    fontSize: "0.85em",
    color: "#795548",
  },
  itemNoteIcon: {
    fontSize: "1em",
    lineHeight: 1,
    flexShrink: 0,
  },
  itemNoteTextWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  itemNoteText: {
    wordBreak: "break-word",
    fontWeight: "600",
  },
  paymentItem: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "10px",
    marginBottom: "8px",
    backgroundColor: "#f9f9f9",
    transition: "all 0.2s ease",
  },
  discountLine: {
    display: "flex",
    justifyContent: "space-between",
    color: "#e53935",
    fontSize: "0.85em",
    fontWeight: "600",
    marginTop: "6px",
    padding: "3px 0",
  },
  discountLabel: {
    marginRight: "8px",
  },
  discountValue: {
    color: "#e53935",
    fontWeight: "600",
  },
  clientContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    color: "#555",
    fontSize: "0.9em",
  },
  statusBox: {
    padding: "4px 10px",
    borderRadius: "5px",
    fontSize: "0.75em",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  voidBadge: {
    position: "absolute",
    top: "8px",
    right: "8px",
    background: "#ff4757",
    color: "white",
    padding: "3px 10px",
    borderRadius: "5px",
    fontSize: "0.7em",
    fontWeight: "bold",
  },
  expandableSection: {
    marginTop: "8px",
    fontSize: "0.85em",
    color: "#666",
    padding: "10px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    border: "1px solid #e9ecef",
  },
  cancelledText: {
    textDecoration: "line-through",
    color: "#95a5a6",
  },
  loadingSpinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px'
  },
  pendingSection: {
    marginBottom: "20px",
    backgroundColor: "#f8f4ff",
    borderRadius: "10px",
    padding: "15px",
    border: "2px solid #9C27B0",
  },
  pendingHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "2px dashed #9C27B0",
  },
  pendingIcon: {
    width: "24px",
    height: "24px",
    marginRight: "10px",
    color: "#9C27B0",
  },
  pendingTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#9C27B0",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  pendingCount: {
    backgroundColor: "#9C27B0",
    color: "white",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "12px",
    marginLeft: "10px",
  },
  confirmedSection: {
    marginBottom: "20px",
    backgroundColor: "#f0f8ff",
    borderRadius: "10px",
    padding: "15px",
    border: "2px solid #2196F3",
  },
  confirmedHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "2px dashed #2196F3",
  },
  confirmedIcon: {
    width: "24px",
    height: "24px",
    marginRight: "10px",
    color: "#2196F3",
  },
  confirmedTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2196F3",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  confirmedCount: {
    backgroundColor: "#2196F3",
    color: "white",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "12px",
    marginLeft: "10px",
  },
  newBadge: {
    backgroundColor: "#9C27B0",
    color: "white",
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "4px",
    marginLeft: "8px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  clientGroup: {
    marginBottom: "16px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "12px",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  clientHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "2px solid #3498db",
    backgroundColor: "#f8f9fa",
    padding: "8px 12px",
    borderRadius: "6px 6px 0 0",
    margin: "-12px -12px 10px -12px",
  },
  clientName: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#2c3e50",
    marginRight: "10px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  clientBadge: {
    backgroundColor: "#3498db",
    color: "white",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: "bold",
  },
  clientGroupPending: {
    marginBottom: "16px",
    backgroundColor: "#faf5ff",
    borderRadius: "8px",
    padding: "12px",
    border: "1px solid #d0b0ff",
    boxShadow: "0 2px 4px rgba(156, 39, 176, 0.1)",
  },
  clientHeaderPending: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "2px solid #9C27B0",
    backgroundColor: "#f3e5f5",
    padding: "8px 12px",
    borderRadius: "6px 6px 0 0",
    margin: "-12px -12px 10px -12px",
  },
  clientNamePending: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#9C27B0",
    marginRight: "10px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
};

const additionalStyles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pending-item {
  animation: slideIn 0.3s ease-out;
}

.select-all-button:hover {
  opacity: 0.85;
  transform: scale(1.02);
}

.title-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.title-clickable:hover {
  opacity: 0.7;
}

/* ── Requerimiento: identificar de inmediato el producto recién agregado ── */
@keyframes highlightFlash {
  0% { background-color: #fff59d; box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.7); }
  70% { background-color: #fff59d; box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.5); }
  100% { background-color: inherit; box-shadow: none; }
}
.order-item-just-added {
  animation: highlightFlash 1.8s ease-out;
}

@keyframes newItemToastIn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
.new-item-toast {
  animation: newItemToastIn 0.2s ease-out;
}

/* ── Requerimiento: Ticket de Venta en formato tabla (aprovechar ancho) ──
   Puramente visual: reestructura las mismas celdas de renderOrderItem en
   columnas de grid en vez de apilarlas en una tarjeta. Por debajo de
   768px (paneles a pantalla completa en móvil/tablet, ver App.css) se
   colapsa de vuelta a una columna, replicando el orden y apariencia del
   diseño original en pantallas angostas. No cambia ningún handler ni
   cálculo — solo el markup/CSS de presentación.

   ── Fix responsive (columnas encimadas/recortadas con zoom) ──
   Antes las columnas numéricas usaban anchos fijos en px (56/72/110/92).
   El zoom del navegador reduce el ancho disponible en px CSS del panel
   (.resumen-panel es un % del layout), así que esas columnas fijas
   dejaban de caber y el contenido se encimaba o se recortaba contra el
   borde del panel. Ahora las columnas usan minmax() con un piso mínimo:
   se angostan de forma fluida y, si aun así no caben, ".ticket-table-scroll"
   habilita scroll horizontal SOLO dentro de la tabla del resumen — la
   página completa nunca se desplaza. Por debajo de 768px se colapsa a
   tarjetas apiladas con etiqueta visible por campo (vía atributo
   data-label, sin tocar datos ni lógica). */
.ticket-table-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.ticket-col-header,
.ticket-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(46px, 60px) minmax(64px, 92px) minmax(90px, 130px) minmax(80px, 112px);
  gap: 8px;
  min-width: 430px;
  box-sizing: border-box;
}
.ticket-col-header {
  padding: 4px 10px 6px;
  font-size: 11px;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.ticket-row {
  align-items: start;
}
.ticket-col-header--with-client,
.ticket-row--with-client {
  grid-template-columns: minmax(56px, 74px) minmax(130px, 1fr) minmax(46px, 60px) minmax(64px, 92px) minmax(90px, 130px) minmax(80px, 112px);
  min-width: 490px;
}
.ticket-cell-qty,
.ticket-cell-price {
  text-align: right;
  white-space: nowrap;
}
.ticket-cell-status {
  display: flex;
  justify-content: flex-end;
}
/* Requerimiento "Notas": botón por producto, dentro de la celda Producto
   (no agrega columna nueva a la tabla). Estilo distinto según tenga nota,
   esté disponible o esté bloqueado por ya haber sido enviado. */
.ticket-note-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  border: 1.5px dashed #f0ad4e;
  background: #fff8ec;
  color: #b9770e;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 14px;
  cursor: pointer;
  min-height: 26px;
  touch-action: manipulation;
  font-family: inherit;
}
.ticket-note-btn:hover {
  background: #fef1d8;
}
.ticket-note-btn--has-note {
  border-style: solid;
  background: #fff3cd;
  color: #8a5a00;
}
.ticket-note-btn--locked,
.ticket-note-btn:disabled {
  border-color: #cbd5e1;
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
  opacity: 0.85;
}
.ticket-note-btn:disabled:hover {
  background: #f1f5f9;
}
@media (max-width: 768px) {
  /* Área táctil cómoda para el botón de nota en móvil (mínimo ~40px alto) */
  .ticket-note-btn {
    min-height: 38px;
    padding: 8px 14px;
    font-size: 12px;
  }
}
@media (max-width: 768px) {
  .ticket-table-scroll {
    overflow-x: visible;
  }
  .ticket-col-header {
    display: none;
  }
  .ticket-row,
  .ticket-row--with-client {
    grid-template-columns: 1fr;
    min-width: 0;
    row-gap: 2px;
  }
  .ticket-cell-qty,
  .ticket-cell-price {
    text-align: left;
  }
  .ticket-cell-status {
    justify-content: flex-start;
  }
  /* Etiquetas visibles por campo en la tarjeta móvil (Producto, Cantidad,
     Precio, Total, Estado). Puramente presentacional vía CSS attr():
     no agrega, quita ni recalcula ningún dato. */
  .ticket-row [data-label],
  .ticket-row--with-client [data-label] {
    padding: 4px 0;
  }
  .ticket-row [data-label]::before,
  .ticket-row--with-client [data-label]::before {
    content: attr(data-label) ": ";
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #7f8c8d;
    margin-right: 6px;
  }
  /* Área táctil más cómoda para el toggle de detalle del total */
  [data-label="Total"] > div:first-child {
    padding: 10px 6px !important;
  }
}
`;

// ============ COMPONENTE PRINCIPAL ORDERSUMMARY CORREGIDO ============
const OrderSummary = ({
  order,
  onSelectItem,
  onSelectMultiple,
  selectedIndex,
  selectedIndices = [],
  appliedPayments,
  onCalculateTotals,
  selectedClient,
  selectedPaymentIndex,
  onSelectPayment,
  isLoading,
  property,
  numeroCheque,
  sendingItems = [],
  // Requerimiento "Order Summary": cuando se agrega un producto, el padre
  // manda una señal { index, name, ts } con el índice (originalIndex) del
  // ítem afectado (nuevo o con cantidad incrementada). "ts" cambia siempre
  // aunque el índice se repita, para poder disparar el efecto cada vez.
  lastAddedSignal = null,
  // Requerimiento "Notas": callback opcional para pedirle al padre (App.js)
  // que abra el panel de notas para el índice de este producto (o para la
  // selección múltiple activa, si el padre decide aplicarlo así). Toda la
  // validación de "ya fue enviado" y el guardado real viven en App.js —
  // aquí solo se dispara la solicitud, no se decide ni se aplica nada.
  onRequestNote = null,
}) => {
  const [internalSelectedIndices, setInternalSelectedIndices] = useState([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [hasValidatedCheque, setHasValidatedCheque] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [addedToast, setAddedToast] = useState(null); // { text }
  const itemNodesRef = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const lastSignalTsRef = useRef(null);
  
  // ✅ FORZAR ACTUALIZACIÓN CUANDO ORDER CAMBIA
  const [forceRender, setForceRender] = useState(0);
  
  // ✅ REFERENCIA PARA DETECTAR CAMBIOS EN ORDER
  const prevOrderRef = useRef();
  
  // ✅ DETECTAR CUALQUIER CAMBIO EN ORDER Y FORZAR RENDER
  useEffect(() => {
    if (prevOrderRef.current !== order) {
      console.log('🔄 OrderSummary: Order cambió, forzando actualización');
      prevOrderRef.current = order;
      setForceRender(prev => prev + 1);
    }
  }, [order]);
  
  // ✅ TAMBIÉN DETECTAR CAMBIOS EN LA LONGITUD
  useEffect(() => {
    console.log('📦 OrderSummary: Longitud del order:', order?.length);
  }, [order?.length, forceRender]);
  
  const currentSelectedIndices = selectedIndices.length > 0 ? selectedIndices : internalSelectedIndices;

  // NORMALIZAR ORDER - con dependencia en forceRender
  const normalizedOrder = useMemo(() => {
    console.log('🔄 OrderSummary: Normalizando order, forceRender:', forceRender);
    
    if (!order || !Array.isArray(order)) return [];
    
    const voidedOrCancelledIds = new Set();
    const cancellationMap = new Map();
    
    order.forEach((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const estado = item.estado || "";
      
      if (quantity < 0 || estado.toLowerCase() === "void") {
        const originalItemId = item.item;
        if (originalItemId) {
          cancellationMap.set(originalItemId, item);
          voidedOrCancelledIds.add(item.id_producto);
          voidedOrCancelledIds.add(originalItemId);
        } else {
          voidedOrCancelledIds.add(item.id_producto);
        }
      }
    });
    
    const normalized = order.map((item, index) => {
      let clientValue = item.client;
      const isFromDB = !!(item.id_producto && typeof item.id_producto === 'number' && item.id_producto > 0);
      const quantity = parseFloat(item.quantity) || 0;
      const estado = item.estado || "";
      
      const isCancelledByQuantity = quantity < 0;
      const isVoidedByStatus = estado.toLowerCase() === "void";
      const isMarkedAsCancelled = voidedOrCancelledIds.has(item.id_producto);
      
      const isCancelled = isCancelledByQuantity || isVoidedByStatus;
      const isVoided = isVoidedByStatus;
      const cancelsOriginalItemId = item.item;
      
      let displayClient = clientValue;
      // El estado siempre usa 0-based para client (normalizado al cargar de DB y en ítems nuevos)
      // → siempre sumar 1 para mostrar número de asiento 1-based al usuario
      if (clientValue !== undefined && clientValue !== null && clientValue >= 0) {
        displayClient = clientValue + 1;
      }
      
      let cargoValue = getSafeValue(item.cargo, 0);
      if (cargoValue === 0) cargoValue = getSafeValue(item.cargo_servicio, 0);
      if (cargoValue === 0) cargoValue = getSafeValue(item.cs, 0);
      
      let montoDescuento = Math.abs(parseFloat(item.monto_descuento) || 0);
      if (montoDescuento === 0 && item.discount) {
        montoDescuento = Math.abs(parseFloat(item.discount.monto) || 0);
        if (montoDescuento === 0 && item.discount.price > 0) {
          const priceTotal = Math.abs(parseFloat(item.price) || 0) * Math.abs(quantity || 1);
          montoDescuento = (priceTotal * item.discount.price) / 100;
        }
      }
      
      return {
        ...item,
        originalIndex: index,
        price: Math.abs(parseFloat(item.price) || 0),
        quantity: quantity,
        modifiers: Array.isArray(item.modifiers) ? item.modifiers : [],
        discount: item.discount || { price: 0, nombre_descuento: item.nombre_descuento || "", monto: montoDescuento },
        monto_descuento: montoDescuento,
        descuento_manual: Math.abs(parseFloat(item.descuento_manual) || 0),
        isCancelled: isCancelled,
        isVoided: isVoided,
        cancelsOriginalItemId: cancelsOriginalItemId,
        estado: isCancelled ? "CANCELADO" : (estado || "PENDIENTE"),
        esProductoDB: isFromDB,
        esProductoNuevo: (!item.id_producto || item.id_producto < 0 || item.isNew === true),
        displayClient: displayClient,
        cargo: cargoValue,
        uniqueId: item.id || `temp-${index}-${Date.now()}-${Math.random()}`,
        originalProductId: item.id_producto,
      };
    });
    
    return normalized;
  }, [order, forceRender]);

  // Requerimiento "Order Summary": al agregar un producto, desplazar la
  // lista hasta él automáticamente y resaltarlo unos segundos, además de
  // mostrar un aviso flotante (sticky) por si el mesero está viendo otra
  // parte del pedido. No toca cálculos, promos, descuentos ni impresión —
  // es puramente visual.
  useEffect(() => {
    if (!lastAddedSignal || lastAddedSignal.ts == null) return;
    if (lastSignalTsRef.current === lastAddedSignal.ts) return; // ya procesada
    lastSignalTsRef.current = lastAddedSignal.ts;

    const targetIndex = lastAddedSignal.index;
    const node = itemNodesRef.current.get(targetIndex);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    setHighlightedIndex(targetIndex);
    setAddedToast({
      text: lastAddedSignal.name ? `Agregado: ${lastAddedSignal.name}` : "Producto agregado",
    });

    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedIndex(null);
      setAddedToast(null);
    }, 1800);
  }, [lastAddedSignal]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  // Validación automática - solo una vez
  useEffect(() => {
    const validateData = async () => {
      if (numeroCheque && property && !hasValidatedCheque) {
        try {
          console.log(`[OrderSummary] Validación única para cheque: ${numeroCheque}`);
          await validarDatosCheque(numeroCheque, property);
          setHasValidatedCheque(true);
        } catch (error) {
          console.error('[OrderSummary] Error en validación:', error);
          setHasValidatedCheque(true);
        }
      }
    };
    validateData();
  }, [numeroCheque, property, hasValidatedCheque]);

  const isItemSelectable = (item) => {
    if (!item) return false;
    if (item.isVoided) return false;
    if (item.isCancelled) return false;
    if (item.quantity < 0) return false;
    return true;
  };

  const toggleSelection = (index) => {
    const item = normalizedOrder[index];
    if (!isItemSelectable(item)) return;
    
    setInternalSelectedIndices(prev => {
      let newSelection;
      if (prev.includes(index)) {
        newSelection = prev.filter(i => i !== index);
      } else {
        newSelection = [...prev, index];
      }
      
      if (onSelectMultiple) {
        onSelectMultiple(newSelection);
      }
      
      if (newSelection.length === 1 && onSelectItem) {
        onSelectItem(newSelection[0]);
      } else if (newSelection.length === 0 && onSelectItem) {
        onSelectItem(null);
      }
      
      return newSelection;
    });
  };

  const selectRange = (endIndex) => {
    if (currentSelectedIndices.length === 0) return;
    const startIndex = currentSelectedIndices[currentSelectedIndices.length - 1];
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    
    const currentSet = new Set(currentSelectedIndices);
    
    for (let i = start; i <= end; i++) {
      if (normalizedOrder[i] && isItemSelectable(normalizedOrder[i])) {
        currentSet.add(i);
      }
    }
    
    const newSelection = Array.from(currentSet).sort((a, b) => a - b);
    
    setInternalSelectedIndices(newSelection);
    
    if (onSelectMultiple) {
      onSelectMultiple(newSelection);
    }
    
    if (newSelection.length === 1 && onSelectItem) {
      onSelectItem(newSelection[0]);
    }
  };

  const handleSelectItem = (index, event) => {
    if (!normalizedOrder[index]) return;
    const item = normalizedOrder[index];
    
    if (!isItemSelectable(item)) {
      return;
    }
    
    const isCtrlPressed = event?.ctrlKey || event?.metaKey;
    const isShiftPressed = event?.shiftKey;
    
    if (isShiftPressed && currentSelectedIndices.length > 0) {
      selectRange(index);
    } 
    else {
      toggleSelection(index);
    }
  };

  const clearSelection = () => {
    setInternalSelectedIndices([]);
    if (onSelectMultiple) onSelectMultiple([]);
    if (onSelectItem) onSelectItem(null);
  };

  const selectAll = () => {
    const allIndices = normalizedOrder
      .map((item, idx) => isItemSelectable(item) ? idx : null)
      .filter(idx => idx !== null);
    
    setInternalSelectedIndices(allIndices);
    if (onSelectMultiple) onSelectMultiple(allIndices);
    if (allIndices.length === 1 && onSelectItem) onSelectItem(allIndices[0]);
  };

  const deselectAll = () => clearSelection();

  const groupItemsByClient = (items) => {
    const grouped = {};
    const sortedItems = [...items].sort((a, b) => {
      const clientA = a.displayClient !== undefined ? a.displayClient : -1;
      const clientB = b.displayClient !== undefined ? b.displayClient : -1;
      return clientA - clientB;
    });
    
    sortedItems.forEach(item => {
      const clientNumber = item.displayClient !== undefined ? item.displayClient : 'Sin asignar';
      const clientKey = `Cliente ${clientNumber}`;
      if (!grouped[clientKey]) grouped[clientKey] = [];
      grouped[clientKey].push(item);
    });
    
    return grouped;
  };

  const {
    activeGroups,
    cancelledItems,
    pendingGroups,
    hasActive,
    hasCancelled,
    hasPending
  } = useMemo(() => {
    if (!normalizedOrder.length) {
      return { activeGroups: {}, cancelledItems: [], pendingGroups: {}, hasActive: false, hasCancelled: false, hasPending: false };
    }

    const active = normalizedOrder.filter(item =>
      !item.isCancelled && !item.isVoided && item.quantity >= 0
    );

    // ✅ Separar productos pendientes (nuevos, sin ordenar) de confirmados (ya en BD)
    const pending = active.filter(item => item.esProductoNuevo);
    const confirmed = active.filter(item => !item.esProductoNuevo);

    const cancelled = normalizedOrder.filter(item =>
      item.isCancelled || item.isVoided || item.quantity < 0
    );

    const pendingGrouped = groupItemsByClient(pending);
    const confirmedGrouped = groupItemsByClient(confirmed);

    return {
      activeGroups: confirmedGrouped,
      pendingGroups: pendingGrouped,
      cancelledItems: cancelled,
      hasActive: Object.keys(confirmedGrouped).length > 0,
      hasCancelled: cancelled.length > 0,
      hasPending: Object.keys(pendingGrouped).length > 0,
    };
  }, [normalizedOrder]);

  const orderCalculations = useMemo(() => {
    let totalSinDescuento = 0;
    let totalDescuentos = 0;
    let totalImpuestos = 0;
    let totalCargos = 0;
    let subtotal = 0;

    normalizedOrder.forEach((item) => {
      if (!item.isCancelled && !item.isVoided && item.quantity > 0) { // ✅ FIX: cantidad=0 no afecta el cálculo
        const itemTotal = calculateItemTotal(item);
        totalSinDescuento += (item.price * Math.abs(item.quantity)) || 0; // ✅ FIX: precio inclusive × cantidad
        totalDescuentos += itemTotal.totalDiscount || 0;
        subtotal += itemTotal.subtotalBeforeTaxes || 0;
        totalImpuestos += itemTotal.taxAmount || 0;
        totalCargos += itemTotal.cargoAmount || 0;
      }
    });

    const totalCalculado = subtotal + totalImpuestos + totalCargos;
    
    return {
      totalSinDescuento,
      totalDescuentos,
      totalImpuestos,
      totalCargos,
      subtotal,
      totalCalculado,
    };
  }, [normalizedOrder]);

  // Notificar totales cuando cambien
  useEffect(() => {
    if (onCalculateTotals && normalizedOrder.length > 0) {
      const totalsData = {
        ...orderCalculations,
        order: normalizedOrder.map((item, idx) => {
          const itemTotal = !item.isCancelled && !item.isVoided ? calculateItemTotal(item) : { totalWithTaxes: 0 };
          return {
            id: item.id,
            id_producto: item.id_producto,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            tax: getSafeValue(item.tax),
            cargo: getSafeValue(item.cargo),
            impuesto: (itemTotal.taxAmount || 0).toFixed(2),
            cargoServicio: (itemTotal.cargoAmount || 0).toFixed(2),
            total: (itemTotal.totalWithTaxes || 0).toFixed(2),
            modifiers: item.modifiers,
            client: item.displayClient ?? selectedClient + 1,
            isCancelled: item.isCancelled,
            isVoided: item.isVoided,
            estado: item.estado,
            originalIndex: idx,
          };
        }),
        selectedIndices: currentSelectedIndices,
      };
      onCalculateTotals(totalsData);
    }
  }, [normalizedOrder, selectedClient, onCalculateTotals, orderCalculations, currentSelectedIndices]);

  // Inyectar estilos
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = additionalStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const isSelected = (index) => currentSelectedIndices.includes(index);
  
  const selectableItems = normalizedOrder.filter(item => isItemSelectable(item));
  const selectableCount = selectableItems.length;
  const selectedCount = currentSelectedIndices.filter(idx => {
    const item = normalizedOrder[idx];
    return item && isItemSelectable(item);
  }).length;
  const allSelectableSelected = selectableCount > 0 && selectedCount === selectableCount;

  const renderOrderItem = (item, index, isActive = true) => {
    if (!item) return null;
    
    const isCancelledOrVoided = item.isCancelled || item.isVoided || item.quantity < 0;
    const isNegativeQuantity = item.quantity < 0;
    
    const itemTotal = !isCancelledOrVoided ? calculateItemTotal(item) : { totalWithTaxes: 0, totalDiscount: 0 };
    const totalWithTaxes = itemTotal?.totalWithTaxes ?? 0;
    const totalDiscount = itemTotal?.totalDiscount ?? 0;
    const statusStyle = getStatusStyle(item.estado, false);
    const isSending = sendingItems.includes(item.id_producto) || sendingItems.includes(item.uniqueId);
    const selected = isSelected(index);
    const selectable = isItemSelectable(item);

    // Requerimiento "Notas": solo los productos pendientes (aún no enviados
    // a BD/cocina/KDS) pueden recibir nota nueva o editada — mismo criterio
    // que ya usa esta pantalla para agrupar "sin ordenar" vs "confirmados".
    // No se recalcula ni redefine acá: esProductoNuevo ya viene calculado
    // en normalizedOrder.
    const canEditNote = item.esProductoNuevo === true;
    const hasNote = Array.isArray(item.modifiers) && item.modifiers.some(isNoteModifier);

    let originalItem = null;
    if (item.cancelsOriginalItemId) {
      originalItem = normalizedOrder.find(ord => ord.id_producto === item.cancelsOriginalItemId || ord.originalProductId === item.cancelsOriginalItemId);
    }

    return (
      <li
        key={item.uniqueId || index}
        ref={(el) => {
          if (el) itemNodesRef.current.set(index, el);
          else itemNodesRef.current.delete(index);
        }}
        className={`ticket-row${!isActive ? " ticket-row--with-client" : ""}${highlightedIndex === index ? " order-item-just-added" : ""}`}
        onClick={(e) => {
          if (selectable) {
            e.stopPropagation();
            handleSelectItem(index, e);
          }
        }}
        style={{
          ...styles.orderItem,
          ...(selected && selectable && styles.selectedMultiple),
          ...(selected && !selectable && { cursor: 'not-allowed', opacity: 0.6 }),
          ...(item.isVoided && styles.voidedItem),
          ...((isCancelledOrVoided && !isNegativeQuantity) && styles.cancelledItem),
          ...(isNegativeQuantity && { borderLeft: "4px solid #ff9800", backgroundColor: "#fff8e1" }),
          opacity: isSending ? 0.7 : (selected ? 1 : (selectable ? 1 : 0.8)),
          cursor: selectable ? "pointer" : "not-allowed",
        }}
      >
        {selected && selectable && <div style={styles.selectedMultipleBadge}>✓</div>}

        {/* Requerimiento: los badges de estado (ANULADO/CANCELADO/CANCELACIÓN)
            iban con position:absolute pensados para la tarjeta alta original;
            en la fila compacta de la tabla eso los sacaba del área visible
            de la fila. La misma información ya se muestra en flujo normal
            en la Columna Estado, así que aquí se quita el duplicado flotante
            en vez de reposicionarlo — no se pierde el dato, solo se deja de
            repetir fuera de lugar. */}

        {isSending && (
          <div style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            background: "#f39c12",
            color: "white",
            padding: "3px 10px",
            borderRadius: "5px",
            fontSize: "0.7em",
            fontWeight: "bold",
            animation: "pulse 1.5s infinite",
          }}>ENVIANDO...</div>
        )}

        {/* Requerimiento: el dato de cliente ya lo indica el encabezado del
            grupo (renderClientGroup), así que en filas agrupadas se omite
            para no repetirlo. Se conserva solo en la lista de cancelados/
            anulados, que no está agrupada por cliente. */}
        {!isActive && (
          <div style={{ fontSize: "12px", color: "#555" }}>
            <strong>Cliente:</strong> {item.displayClient !== undefined ? `${item.displayClient}` : "Sin asignar"}
          </div>
        )}

        {/* Columna Producto: nombre, relación de cancelación, modificadores, descuento */}
        <div data-label="Producto" style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: "600",
            textDecoration: isCancelledOrVoided && !isNegativeQuantity ? "line-through" : "none",
            color: isCancelledOrVoided && !isNegativeQuantity ? "#95a5a6" : (isNegativeQuantity ? "#ff9800" : "#2c3e50"),
            fontSize: "15px"
          }}>
            {item.name}
            {isNegativeQuantity && (
              <span style={{ fontSize: "10px", color: "#ff9800", marginLeft: "5px", fontWeight: "normal" }}>
                (CANCELA producto original)
              </span>
            )}
          </div>

          {item.cancelsOriginalItemId && originalItem && (
            <div style={{
              marginTop: "4px",
              marginBottom: "8px",
              padding: "6px 8px",
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              fontSize: "0.75em",
              fontFamily: "monospace",
              borderLeft: "3px solid #ffc107"
            }}>
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                🔄 <strong>Relación de Cancelación:</strong>
              </div>
              <div>
                Este registro con <span style={{ color: "#d32f2f", fontWeight: "bold" }}>cantidad {item.quantity}</span>
                {" → CANCELA al producto original: "}
                <span style={{ textDecoration: "line-through", color: "#856404" }}>
                  "{originalItem.name}"
                </span>
              </div>
              <div style={{ fontSize: "0.85em", marginTop: "4px", color: "#666" }}>
                📊 Detalle original: {originalItem.quantity} x ${originalItem.price.toFixed(2)} = ${(originalItem.price * Math.abs(originalItem.quantity)).toFixed(2)}
              </div>
            </div>
          )}

          {!isCancelledOrVoided && (
            <>
              {onRequestNote && (
                <button
                  type="button"
                  className={`ticket-note-btn${hasNote ? " ticket-note-btn--has-note" : ""}${!canEditNote ? " ticket-note-btn--locked" : ""}`}
                  // Requerimiento: un producto ya enviado NO debe poder abrir
                  // el panel de notas — se bloquea el botón mismo (disabled
                  // nativo), no solo su estilo. Así no depende de que el
                  // click llegue a validarse más arriba.
                  disabled={!canEditNote}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canEditNote) return;
                    onRequestNote(index);
                  }}
                  aria-disabled={!canEditNote}
                  aria-label={
                    !canEditNote
                      ? "Nota no disponible: producto ya enviado"
                      : (hasNote ? "Agregar otra nota al producto (ya tiene una)" : "Agregar nota al producto")
                  }
                  title={
                    !canEditNote
                      ? "No es posible modificar la nota: este producto ya fue enviado"
                      : (hasNote ? "Este producto ya tiene nota — toca para agregar otra" : "Agregar nota")
                  }
                >
                  <i className="bi bi-sticky" aria-hidden="true" /> Agregar nota
                </button>
              )}
              <ModifiersDisplay modifiers={item.modifiers} itemQuantity={item.quantity} />
              {Math.abs(totalDiscount) > 0 && (
                <DiscountDisplay discount={item.discount} totalDiscount={totalDiscount} itemQuantity={item.quantity} />
              )}
            </>
          )}
        </div>

        {/* Columna Cant */}
        <div className="ticket-cell-qty" data-label="Cantidad" style={{
          color: isCancelledOrVoided && !isNegativeQuantity ? "#95a5a6" : (isNegativeQuantity ? "#ff9800" : "#2c3e50"),
          fontWeight: isNegativeQuantity ? "bold" : "500",
          fontSize: "13px",
        }}>
          {item.quantity < 0 ? `${item.quantity}` : Math.abs(item.quantity).toFixed(2).replace(/\.?0+$/, '')}
        </div>

        {/* Columna Precio: precio unitario + subtotal de línea (mismo dato que mostraba la fila combinada antes) */}
        <div className="ticket-cell-price" data-label="Precio" style={{
          color: isCancelledOrVoided && !isNegativeQuantity ? "#95a5a6" : (isNegativeQuantity ? "#ff9800" : "#2c3e50"),
          fontSize: "13px",
        }}>
          <div>${item.price.toFixed(2)}</div>
          <div style={{ fontSize: "11px", color: "#7f8c8d" }}>
            = ${(item.price * Math.abs(item.quantity)).toFixed(2)}
          </div>
          {isNegativeQuantity && (
            <div style={{ fontSize: "11px", color: "#ff9800" }}>
              → Valor: $0.00
            </div>
          )}
        </div>

        {/* Columna Total (detalle expandible, sin cambios de lógica) */}
        <div data-label="Total">
          <ExpandableDetails item={item} totalWithTaxes={totalWithTaxes} originalItem={originalItem} />
        </div>

        {/* Columna Estado */}
        <div className="ticket-cell-status" data-label="Estado">
          {statusStyle && !isCancelledOrVoided && (
            <div style={{...styles.statusBox, backgroundColor: statusStyle.backgroundColor, color: statusStyle.color}}>
              {statusStyle.text}
            </div>
          )}
          {isCancelledOrVoided && (
            <div style={{...styles.statusBox, backgroundColor: "#e74c3c", color: "white"}}>
              {item.isVoided ? "ANULADO" : (isNegativeQuantity ? "CANCELACIÓN" : "CANCELADO")}
            </div>
          )}
        </div>
      </li>
    );
  };

  const renderClientGroup = (clientName, items) => {
    const totalItems = items.length;
    const totalPorCliente = items.reduce((sum, item) => {
      if (!item.isCancelled && !item.isVoided) {
        const itemTotal = calculateItemTotal(item);
        return sum + (itemTotal?.totalWithTaxes || 0);
      }
      return sum;
    }, 0);
    
    const totalActiveItems = items.length;

    return (
      <div style={{ marginBottom: "10px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "5px 10px",
          marginBottom: "4px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          borderLeft: "3px solid #3498db",
        }}>
          <span style={{ fontSize: "13px", fontWeight: "bold", color: "#2c3e50", display: "flex", alignItems: "center", gap: "4px" }}>
            <span>👤</span> {clientName}
          </span>
          <span style={styles.clientBadge}>
            {totalActiveItems} {totalActiveItems === 1 ? 'producto' : 'productos'}
          </span>
          <span style={{ marginLeft: "auto", fontSize: "13px", fontWeight: "600", color: "#2c3e50" }}>
            ${totalPorCliente.toFixed(2)}
          </span>
        </div>
        <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
          {items.map((item) => renderOrderItem(item, item.originalIndex, true))}
        </ul>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={styles.orderSummary}>
        <h2 style={{ textAlign: "center", fontSize: "20px", marginBottom: "15px", color: "#2c3e50", fontWeight: "600" }}>
          Resumen del Pedido
        </h2>
        <div style={{ textAlign: "center", color: "#7f8c8d", fontSize: "14px", padding: "20px" }}>
          <div style={styles.loadingSpinner}></div>
          Cargando pedido...
        </div>
      </div>
    );
  }

  if (!normalizedOrder?.length) {
    return (
      <div style={styles.orderSummary}>
        <h2 style={{ textAlign: "center", fontSize: "20px", marginBottom: "15px", color: "#2c3e50", fontWeight: "600" }}>
          Resumen del Pedido
        </h2>
        <p style={{ textAlign: "center", color: "#7f8c8d", fontSize: "14px" }}>No hay productos en el pedido.</p>
      </div>
    );
  }

  return (
    <div style={styles.orderSummary}>
      <style>{additionalStyles}</style>

      {/* Aviso flotante "sticky": si el mesero está viendo otra parte del
          pedido (scrolleado hacia abajo), este aviso se mantiene visible
          arriba del área visible en vez de perderse fuera de pantalla. */}
      {addedToast && (
        <div
          className="new-item-toast"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            marginBottom: "10px",
            backgroundColor: "#43a047",
            color: "white",
            padding: "8px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "600",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          ✅ {addedToast.text}
        </div>
      )}

      <div style={styles.headerWithSelectAll}>
        <h2 
          onClick={selectableCount > 0 ? (allSelectableSelected ? deselectAll : selectAll) : undefined}
          style={{ 
            fontSize: "20px", 
            margin: "0", 
            color: "#2c3e50", 
            fontWeight: "600",
            cursor: selectableCount > 0 ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          title={selectableCount > 0 ? (allSelectableSelected ? "Clic para deseleccionar todos" : "Clic para seleccionar todos") : "No hay productos seleccionables"}
        >
          📋 Resumen del Pedido
          {selectableCount > 0 && (
            <span style={{ fontSize: "12px", backgroundColor: "#e0e0e0", padding: "2px 8px", borderRadius: "12px", fontWeight: "normal" }}>
              {allSelectableSelected ? "✓ Todos" : `${selectedCount}/${selectableCount}`}
            </span>
          )}
        </h2>
        
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {selectedCount > 0 && (
            <span style={styles.selectionInfo}>✓ {selectedCount} seleccionado{selectedCount !== 1 ? 's' : ''}</span>
          )}
          {selectedCount > 0 && (
            <button onClick={deselectAll} style={{...styles.selectAllButton, backgroundColor: "#ff9800", padding: "4px 12px", fontSize: "12px"}}>
              🗑️ Limpiar
            </button>
          )}
          {selectableCount > 0 && (
            <button onClick={allSelectableSelected ? deselectAll : selectAll} style={{...styles.selectAllButton, ...(allSelectableSelected ? styles.selectAllButtonDeselect : styles.selectAllButtonSelected), padding: "4px 12px", fontSize: "12px"}}>
              {allSelectableSelected ? "✗ Deseleccionar Todos" : "✓ Seleccionar Todos"}
            </button>
          )}
        </div>
      </div>

      {selectableCount > 0 && (
        <div style={{ marginBottom: "15px", padding: "8px 12px", backgroundColor: "#f5f5f5", borderRadius: "6px", fontSize: "11px", color: "#666", textAlign: "center" }}>
          💡 <strong>Consejo:</strong> Usa <kbd style={{ backgroundColor: "#eee", padding: "2px 6px", borderRadius: "4px" }}>Ctrl</kbd> + Click para selección múltiple · 
          <kbd style={{ backgroundColor: "#eee", padding: "2px 6px", borderRadius: "4px", marginLeft: "4px" }}>Shift</kbd> + Click para selección por rango
        </div>
      )}

      {hasCancelled && (
        <div style={{ marginBottom: "20px", backgroundColor: "#fff5f5", borderRadius: "10px", padding: "15px", border: "2px solid #e74c3c" }}>
          <div 
            onClick={() => setShowCancelled(!showCancelled)}
            style={{ 
              display: "flex", 
              alignItems: "center", 
              marginBottom: showCancelled ? "15px" : "0",
              paddingBottom: showCancelled ? "10px" : "0",
              borderBottom: showCancelled ? "2px dashed #e74c3c" : "none",
              cursor: "pointer",
              userSelect: "none"
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "10px" }}>
              {showCancelled ? "🔽" : "▶️"}
            </span>
            <span style={{ fontSize: "20px", marginRight: "10px" }}>🚫</span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#e74c3c", textTransform: "uppercase" }}>
              Productos Cancelados / Anulados
            </span>
            <span style={{ backgroundColor: "#e74c3c", color: "white", borderRadius: "12px", padding: "2px 8px", fontSize: "12px", marginLeft: "10px" }}>
              {cancelledItems.length}
            </span>
            <span style={{ marginLeft: "auto", fontSize: "11px", color: "#e74c3c" }}>
              {showCancelled ? "▲" : "▼"}
            </span>
          </div>
          
          {showCancelled && (
            <>
              <div style={{ marginBottom: "12px", padding: "8px", backgroundColor: "#ffebee", borderRadius: "6px", fontSize: "12px", color: "#c62828" }}>
                <strong>⚠️ Información:</strong> Estos productos han sido cancelados o anulados. NO afectan el total y NO se pueden seleccionar.
                <br />
                <span style={{ fontSize: "11px" }}>💡 Los registros con cantidad <strong style={{ color: "#ff9800" }}>negativa</strong> son los que cancelan al producto original.</span>
              </div>
              <div className="ticket-table-scroll">
                <div className="ticket-col-header ticket-col-header--with-client">
                  <span>Cliente</span><span>Producto</span><span style={{ textAlign: "right" }}>Cant</span><span style={{ textAlign: "right" }}>Precio</span><span style={{ textAlign: "right" }}>Total</span><span style={{ textAlign: "right" }}>Estado</span>
                </div>
                <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                  {cancelledItems.map((item) => renderOrderItem(item, item.originalIndex, false))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* ✅ SECCIÓN DE PRODUCTOS PENDIENTES (NUEVOS, SIN ORDENAR) */}
      {hasPending && (
        <div style={styles.pendingSection}>
          <div style={styles.pendingHeader}>
            <svg style={styles.pendingIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 13c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z"/>
            </svg>
            <span style={styles.pendingTitle}>🆕 Productos sin ordenar (Por enviar)</span>
            <span style={styles.pendingCount}>
              {Object.values(pendingGroups).reduce((sum, items) => sum + items.length, 0)}
            </span>
          </div>

          <div className="ticket-table-scroll">
            <div className="ticket-col-header">
              <span>Producto</span><span style={{ textAlign: "right" }}>Cant</span><span style={{ textAlign: "right" }}>Precio</span><span style={{ textAlign: "right" }}>Total</span><span style={{ textAlign: "right" }}>Estado</span>
            </div>

            {Object.entries(pendingGroups)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([clientName, items]) => renderClientGroup(clientName, items))}
          </div>
        </div>
      )}

      {/* ✅ SECCIÓN DE PRODUCTOS CONFIRMADOS (YA EN BD) */}
      {hasActive && (
        <div style={styles.confirmedSection}>
          <div style={styles.confirmedHeader}>
            <svg style={styles.confirmedIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span style={styles.confirmedTitle}>✅ Productos confirmados (Ya ordenados)</span>
            <span style={styles.confirmedCount}>
              {Object.values(activeGroups).reduce((sum, items) => sum + items.length, 0)}
            </span>
          </div>

          <div className="ticket-table-scroll">
            <div className="ticket-col-header">
              <span>Producto</span><span style={{ textAlign: "right" }}>Cant</span><span style={{ textAlign: "right" }}>Precio</span><span style={{ textAlign: "right" }}>Total</span><span style={{ textAlign: "right" }}>Estado</span>
            </div>

            {Object.entries(activeGroups)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([clientName, items]) => renderClientGroup(clientName, items))}
          </div>
        </div>
      )}

      <div style={{ marginTop: "20px", borderTop: "2px solid #3498db", paddingTop: "15px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", justifyContent: "flex-end", alignItems: "baseline", marginBottom: "10px" }}>
          <span style={{ fontSize: "13px" }}>
            Total sin descuento: <strong style={{ fontWeight: "500" }}>${orderCalculations.totalSinDescuento.toFixed(2)}</strong>
          </span>
          {orderCalculations.totalDescuentos > 0 && (
            <span style={{ fontSize: "13px", color: "#e53935", fontWeight: "500" }}>
              Descuentos aplicados: -${Math.abs(orderCalculations.totalDescuentos).toFixed(2)}
            </span>
          )}
          <span style={{ fontSize: "13px" }}>
            SubTotal: <strong style={{ fontWeight: "500" }}>${orderCalculations.subtotal.toFixed(2)}</strong>
          </span>
          <span style={{ fontSize: "13px" }}>
            Impuestos: <strong style={{ fontWeight: "500" }}>${orderCalculations.totalImpuestos.toFixed(2)}</strong>
          </span>
          <span style={{ fontSize: "13px" }}>
            Cargos por servicio: <strong style={{ fontWeight: "500" }}>${orderCalculations.totalCargos.toFixed(2)}</strong>
          </span>
        </div>
        <h3 style={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: "10px", borderTop: "2px solid #bdc3c7", paddingTop: "12px", margin: "15px 0 0 0", fontSize: "16px", fontWeight: "600", color: "#2c3e50" }}>
          <span>💰 Total a Pagar:</span>
          <span style={{ color: "#27ae60", fontSize: "20px" }}>${orderCalculations.totalCalculado.toFixed(2)}</span>
        </h3>
      </div>
      
      {appliedPayments && appliedPayments.length > 0 && (
        <div style={{ marginTop: "25px", paddingTop: "15px", borderTop: "2px solid #3498db" }}>
          <h4 style={{ fontSize: "16px", marginBottom: "12px", color: "#2c3e50", fontWeight: "600" }}>💳 Pagos realizados:</h4>
          <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
            {appliedPayments.map((payment, index) => (
              <li key={index} onClick={() => onSelectPayment && onSelectPayment(index)} style={{...styles.paymentItem, ...(selectedPaymentIndex === index && { backgroundColor: "#e3f2fd", border: "1px solid #2196F3" })}}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ fontWeight: "500" }}>{payment.method?.name || "Método no especificado"}</span>
                  <span style={{ fontWeight: "600", color: "#27ae60" }}>${payment.amount.toFixed(2)}</span>
                </div>
                {(payment.tip > 0 || payment.propinaAplicada > 0) && (
                  <div style={{ fontSize: "0.8em", color: "#7f8c8d" }}>💝 Propina: ${(payment.tip || payment.propinaAplicada).toFixed(2)}</div>
                )}
                {payment.change > 0 && <div style={{ fontSize: "0.8em", color: "#7f8c8d" }}>Cambio: ${payment.change.toFixed(2)}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;